<template>
  <div class="fortune-wheel-form">
    <HeadSection
      title=""
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
      :uuid="currentWheelUuid"
    />
    <MainContainer :loading="userLoading || loadingWheel" :permissionOfSection="permissionOfSection">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <h1 class="mr-4 mb-0">{{ currentWheelName }}</h1>
          <a-badge
            :count="currentWheelStatus"
            :number-style="getBadgeStatusStyle(currentWheelStatus)"
            style="transform: scale(1.2);"
          />
        </div>
        <div v-if="editPermission" class="d-flex align-items-center" style="gap: 10px;">
          <a-button
            :disabled="loading"
            size="large"
            @click="goToStatisticsWheel"
            class="statistic-button text-white bg-orange"
          >
            <span class="px-2">Statistics</span>
          </a-button>
          <a-button
            :disabled="loading"
            size="large"
            @click="goToDetailsWheel"
          >
            <span class="px-2">Details</span>
          </a-button>
          <a-button
            :type="isActiveWheel ? 'danger' : 'primary'"
            size="large"
            :disabled="loading"
            :loading="loading"
            @click="toggleStatusWheelModal(true)"
          >
            <span class="px-2">{{ isActiveWheel ? 'Stop' : 'Start' }}</span>
          </a-button>
        </div>
      </div>
      <div v-if="currentWheel" class="d-flex flex-wrap mt-3" style="gap: 140px;">
        <div class="flex flex-column">
          <FormTitle title="General info" class="uppercase" />
          <div class="d-flex flex-wrap" style="gap: 20px">
            <div v-if="!isExternalWheelType" class="d-flex flex-column" style="gap: 15px;">
              <div class="flex flex-column">
                <span class="d-block text-default uppercase">Name</span>
                <span>{{ defaultLanguageInfo.wheel_name }}</span>
              </div>
              <div class="flex flex-column">
                <span class="d-block text-default uppercase">Description</span>
                <span>{{ defaultLanguageInfo.wheel_description }}</span>
              </div>
            </div>
            <div v-if="!isExternalWheelType" style="width: 100px;" />
            <div class="d-flex flex-column">
              <span class="d-block text-default uppercase">Activity info</span>
              <div class="d-flex flex-column" style="min-width: 300px; max-width: 500px;">
                <a-progress :percent="activityInfoPercent" :strokeWidth="15" :show-info="false" />
                <div class="d-flex align-items-center justify-content-between">
                  <div v-if="wheelStartedAt" class="d-flex flex-column">
                    <span class="font-size-15">{{ wheelStartedAt.date }}</span>
                    <span class="d-flex align-items-center font-size-14 text-default">
                      <a-icon type="clock-circle" class="mr-2" /> {{ wheelStartedAt.time }}
                    </span>
                  </div>
                  <div v-if="wheelExpiresAt" class="d-flex flex-column">
                    <span class="font-size-15">{{ wheelExpiresAt.date }}</span>
                    <span class="d-flex align-items-center justify-content-end font-size-14 text-default">
                      <a-icon type="clock-circle" class="mr-2" /> {{ wheelExpiresAt.time }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-4">
            <FormTitle title="Details" class="uppercase" />
            <div class="d-flex flex-wrap" style="gap: 40px;">
              <div v-if="!isExternalWheelType" class="flex flex-column">
                <span class="d-block text-default uppercase">Display type</span>
                <span>{{ displayTypeName }}</span>
              </div>
              <div v-if="!isExternalWheelType" class="flex flex-column">
                <span class="d-block text-default uppercase">Display frequency</span>
                <span>{{ `Every ${currentWheel.show_frequency} ${currentWheel.show_frequency === 1 ? 'day' : 'days'}` }}</span>
              </div>
              <div class="flex flex-column">
                <span class="d-block text-default uppercase">Type</span>
                <span class="capitalize">{{ currentWheel.type }}</span>
              </div>
              <div v-if="currentWheel?.segment?.name" class="flex flex-column">
                <span class="d-block text-default uppercase">Segment</span>
                <span class="capitalize">{{ currentWheel?.segment?.name || '-' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <a-row class="mt-5">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <span class="font-weight-bold font-size-16">Sectors</span>
            <a-tooltip placement="topLeft" arrow-point-at-center>
              <template slot="title">
                <span>The minimum number of sectors to spin the wheel is two. The sum of chances in the sectors must be 100</span>
              </template>
              <a-icon class="d-flex ml-2" type="info-circle" />
            </a-tooltip>
          </div>
          <div class="d-flex align-items-center" style="gap: 10px;">
            <a-button
              v-if="sectorEditingMode"
              type="danger"
              ghost
              size="large"
              icon="stop"
              :disabled="changeSectorLoading"
              style="font-size: 16px; font-weight: 300; border-radius: 2px;"
              @click="cancelChangeSector"
            />
            <a-tooltip placement="topLeft" :getPopupContainer="getPopupContainer">
              <template v-if="isActiveWheel" slot="title">
                <span>Stop the Wheel to edit sector</span>
              </template>
              <a-button
                v-if="isHasSectors"
                :type="sectorEditingMode ? 'primary' : 'default'"
                size="large"
                :class="sectorEditingMode ? 'success-color' : ''"
                :loading="changeSectorLoading"
                :disabled="isActiveWheel"
                style="font-size: 16px; font-weight: 300; border-radius: 2px;"
                @click="changeSectors"
              >
                {{ sectorEditingMode ? 'Save' : 'Edit' }}
              </a-button>
            </a-tooltip>
            <a-tooltip placement="topLeft" :getPopupContainer="getPopupContainer">
              <template v-if="isActiveWheel" slot="title">
                <span>Stop the Wheel to add sector</span>
              </template>
              <a-button
                v-if="createPermission"
                type="primary"
                size="large"
                :disabled="isActiveWheel || sectorEditingMode"
                @click="$router.push(`/fortune-wheel/${currentWheel.id}/create-sector`)"
              >
                Add
              </a-button>
            </a-tooltip>
          </div>
        </div>
        <Sectors
          v-if="currentWheel"
          ref="sectors-table"
          class="mt-3"
          :currentWheelId="currentWheel.id"
          :dataSource="currentWheel?.sectors ? currentWheel.sectors : []"
          :isActiveWheel="isActiveWheel"
          :sectorEditingMode="sectorEditingMode"
          :sectorsSnapshot="sectorsSnapshot"
          :changeSectorLoading="changeSectorLoading"
          :editPermission="editPermission"
          :deletePermission="deletePermission"
          @cancel="cancelChangeSector"
          @equalizeSectors="equalizeSectors"
          @setSnapshot="setSectorsSnapshot"
        />
      </a-row>
    </MainContainer>
    <a-modal
      v-model="appsModal"
      title="Selected apps"
      :footer="null"
      width="700px"
      :destroyOnClose="true"
    >
      <AppsModal
        :modalAppsData="[]"
        :availableApplications="selectedApplications"
        :readonly="true"
        @close="toggleAppModal(false)"
      />
    </a-modal>
    <a-modal
      v-model="changeStatusWheelModal"
      :title="titleStatusWheelModal"
      width="400px"
      centered
      :destroyOnClose="true"
    >
      <p style="margin-bottom: 0;">{{ textStatusWheelModal }}</p>
      <template #footer>
        <a-button
          :disabled="loading"
          @click="toggleStatusWheelModal(false)"
        >
          Cancel
        </a-button>
        <a-button
          :type="actionButtonStatusWheelModal.type"
          :loading="loading"
          @click="changeStatusWheel"
        >
          {{ actionButtonStatusWheelModal.text }}
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import FormTitle from '@/components/custom/form/FormTitle.vue'
import Sectors from '@/views/apps/fortune-wheel/components/Sectors.vue'
import AppsModal from '@/components/custom/modals/appsModal.vue'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { mapState } from 'vuex'
import { getWheelStatusColor } from '@/views/apps/fortune-wheel/composables/useGetWheelStatusColor.js'
import { getActivityPervent } from '@/composables/useGetActivityPervent.js'
import { displayTypeOptions } from '@/views/apps/fortune-wheel/constants/displayTypeOptions.js'
import { getCurrentWheel } from '@/views/apps/fortune-wheel/composables/useGetCurrentWheel.js'
import { UTCFormatInCurrentTimezone } from '@/views/apps/fortune-wheel/composables/useFormatDate.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    FormTitle,
    Sectors,
    AppsModal,
  },
  data: () => ({
    permissionOfSection: 'fortune_wheel',
    currentWheel: null,
    breadcrumbs: [{ name: 'Fortune wheel', link: '/fortune-wheel' }],
    sectorEditingMode: false,
    sectorsSnapshot: [],
    changeSectorLoading: false,
    appsModal: false,
    changeStatusWheelModal: false,
    loadingWheel: true,
    loading: false,
  }),
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    currentWheelId() {
      return this.currentWheel ? this.currentWheel.id : ''
    },
    currentWheelUuid() {
      return this.currentWheel ? this.currentWheel.uuid : ''
    },
    currentWheelName() {
      return this.currentWheel ? this.currentWheel.name : ''
    },
    isActiveWheel() {
      return this.currentWheel?.status === 'active'
    },
    currentWheelStatus() {
      return this.currentWheel?.status
    },
    isExternalWheelType() {
      return this.currentWheel?.type === 'external'
    },
    isHasSectors() {
      return !!this.currentWheel?.sectors?.length
    },
    selectedApplications() {
      return this.currentWheel && this.currentWheel?.applications?.length ? this.currentWheel.applications : []
    },
    sumOfSectorChances() {
      if (!this.currentWheel?.sectors?.length) {
        return false
      }
      const result = this.currentWheel?.sectors.reduce((sum, sector) => {
        return sum + sector.chance
      }, 0)
      return result === 100
    },
    titleStatusWheelModal() {
      return this.isActiveWheel ? 'Stop the Wheel?' : 'Start the Wheel?'
    },
    textStatusWheelModal() {
      return this.isActiveWheel
        ? 'Stopping the wheel will reset its issuance history, making it available to all users again. Are you sure you want to proceed?'
        : 'Once activated, editing will no longer be possible. To make changes, you must stop the wheel, which will reset its issuance history and make it available to all users again.'
    },
    actionButtonStatusWheelModal() {
      return this.isActiveWheel ? { text: 'Stop', type: 'danger' } : { text: 'Start', type: 'primary' }
    },
    defaultLanguageInfo() {
      const defaultLangIndex = this.currentWheel?.languages?.findIndex(lang => lang.is_default)
      if (defaultLangIndex === -1) {
        return {
          wheel_name: '-',
          wheel_description: '-',
        }
      }
      return {
        wheel_name: this.currentWheel.languages[defaultLangIndex].wheel_name,
        wheel_description: this.currentWheel.languages[defaultLangIndex].wheel_description,
      }
    },
    activityInfoPercent() {
      return getActivityPervent(this.currentWheel?.starts_at, this.currentWheel?.ends_at)
    },
    wheelStartedAt() {
      if (!this.currentWheel?.starts_at) {
        return null
      }
      return {
        date: UTCFormatInCurrentTimezone(this.currentWheel.starts_at, 'YYYY-MM-DD HH:mm', 'DD.MM.YYYY'),
        time: UTCFormatInCurrentTimezone(this.currentWheel.starts_at, 'YYYY-MM-DD HH:mm', 'HH:mm'),
      }
    },
    wheelExpiresAt() {
      if (!this.currentWheel?.ends_at) {
        return null
      }
      return {
        date: UTCFormatInCurrentTimezone(this.currentWheel.ends_at, 'YYYY-MM-DD HH:mm', 'DD.MM.YYYY'),
        time: UTCFormatInCurrentTimezone(this.currentWheel.ends_at, 'YYYY-MM-DD HH:mm', 'HH:mm'),
      }
    },
    displayTypeName() {
      if (!this.currentWheel.display_type) {
        return '-'
      }

      const displayTypeObject = displayTypeOptions.find(type => type.value === this.currentWheel.display_type)

      if (!displayTypeObject) {
        return '-'
      }
      return displayTypeObject.name
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    getBadgeStatusStyle(status) {
      return getWheelStatusColor(status)
    },
    async changeStatusWheel() {
      try {
        this.loading = true

        const url = `${wheelApiUrl}/admin/wheels/${this.currentWheel.id}/status`
        const data = {
          status: this.isActiveWheel ? 'inactive' : 'active',
        }
        const response = await apiClient.post(url, data)
        if (response?.data?.success) {
          this.toggleStatusWheelModal(false)
          this.currentWheel.status = this.isActiveWheel ? 'inactive' : 'active'
          this.cancelChangeSector()
          this.$notification.success({
            message: 'Status',
            description: response?.data?.message || '',
          })
        } else {
          getErrorMessage(response?.data)
          this.toggleStatusWheelModal(false)
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    setSectorsSnapshot(data = []) {
      this.sectorsSnapshot = data.map((sector, index) => {
        return { id: sector.id, position: index, chance: sector.chance }
      })
    },
    goToStatisticsWheel() {
      this.$router.push(`/fortune-wheel/${this.currentWheel.id}/statistics`)
    },
    goToDetailsWheel() {
      if (this.isActiveWheel) {
        this.$router.push(`/fortune-wheel/${this.currentWheel.id}/view`)
      } else {
        if (this.editPermission) {
          this.$router.push(`/fortune-wheel/${this.currentWheel.id}/edit`)
        } else {
          this.$router.push(`/fortune-wheel/${this.currentWheel.id}/view`)
          this.$notification.error({
            message: 'You do not have sufficient rights to edit',
          })
        }
      }
    },
    changeSectors() {
      if (this.sectorEditingMode) {
        this.saveSectors()
      } else {
        this.sectorEditingMode = true
      }
    },
    async saveSectors() {
      if (this.$refs['sectors-table']) {
        this.changeSectorLoading = true
        await this.$refs['sectors-table'].changeSectors()
        this.changeSectorLoading = false
      }
    },
    cancelChangeSector(update = true) {
      if (update) {
        this.sectorsSnapshot.forEach((el, index) => {
          this.currentWheel.sectors[index].chance = el.chance
        })
      }
      this.sectorEditingMode = false
    },
    toggleAppModal(status) {
      if (this.loading) {
        return
      }
      this.appsModal = status
    },
    toggleStatusWheelModal(status) {
      this.changeStatusWheelModal = status
    },
    equalizeSectors(sectors) {
      this.currentWheel.sectors = sectors
    },
    async getCurrentWheel() {
      const wheelId = this.$route.params.id
      const data = await getCurrentWheel(wheelId, (status) => {
        this.loadingWheel = status
      })

      if (!data) {
        return
      }

      this.currentWheel = data
      this.setSectorsSnapshot(this.currentWheel?.sectors)
      this.breadcrumbs.push({ name: this.currentWheel.name, link: undefined })
    },
  },
  async created() {
    this.getCurrentWheel()
  },
}
</script>

<style lang="scss">
.fortune-wheel-form {
  .statistic-button {
    &:hover, &:focus {
      border-color: #e9833a;
    }
  }
}
</style>
